import { createContext } from '@shared/utils/createContext';
import React, { useMemo, useRef } from 'react';
import { useHistory } from '@shared/core';
import { activePrintFiltersVar } from './utils/activePrintFiltersCache';
import qs from 'query-string';
import { FILTER_QS_PARSE_OPTIONS } from './utils/deriveActiveFiltersFromUrl';
import { toCount, useFormatSearchParam } from './components/Filters/useFormatSearchParam';

export const convertObjectToQueryParams = (filters: { [facet: string]: string[] }) => {
  return qs.stringify(filters, FILTER_QS_PARSE_OPTIONS);
};

const getQueryStringFromParams = (facetGroupMap: Record<string, string[]>) => {
  const filterAsQueryParam = convertObjectToQueryParams(facetGroupMap);
  const baseUrl = `${location.pathname}${location.search}${location.hash}`;

  return qs.stringifyUrl(
    {
      url: baseUrl,
      query: {
        pf: filterAsQueryParam,
        page: null
      }
    },
    { skipNull: true, skipEmptyString: true }
  );
};

type DesignGalleryFiltersProviderContext = {
  clearAllFilters: () => void;
  getActiveFilterCountByGroupCode: (groupCode: string) => number;
  getTotalActiveFilterCount: () => number;
  setActiveFilter(compositeCode: string, checked: boolean): void;
};
const [Provider, useDesignGalleryFiltersContext] = createContext<DesignGalleryFiltersProviderContext>({ name: 'StationeryFiltersProvider' });

export const DesignGalleryFiltersProvider: React.FC = ({ children }) => {
  const formatSearchParam = useFormatSearchParam();
  const formatSearchParamRef = useRef(formatSearchParam);
  formatSearchParamRef.current = formatSearchParam;
  const history = useHistory();

  const ctx = useMemo<DesignGalleryFiltersProviderContext>(() => {
    return {
      clearAllFilters() {
        const [, setFormat] = formatSearchParamRef.current;
        setFormat();
        history.push(getQueryStringFromParams({}));
      },
      getActiveFilterCountByGroupCode(groupCode: string) {
        return Array.from(activePrintFiltersVar()).filter(compositeCode => compositeCode.startsWith(`${groupCode}:`)).length;
      },
      getTotalActiveFilterCount() {
        const [format] = formatSearchParamRef.current;
        return toCount(format) + activePrintFiltersVar().size;
      },
      setActiveFilter(compositeCode: string, checked: boolean) {
        const clonedActivePrintFilters = new Set(activePrintFiltersVar());
        if (checked) {
          clonedActivePrintFilters.add(compositeCode);
        } else {
          clonedActivePrintFilters.delete(compositeCode);
        }

        const facetGroupMap = {} as Record<string, string[]>;
        for (const filterCompositeCode of Array.from(clonedActivePrintFilters)) {
          const [groupCode, valueCode] = filterCompositeCode.split(':');
          facetGroupMap[groupCode] = facetGroupMap[groupCode] || [];
          facetGroupMap[groupCode].push(valueCode);
        }

        history.push(getQueryStringFromParams(facetGroupMap));
      }
    };
  }, [history]);
  return <Provider value={ctx}>{children}</Provider>;
};

export { useDesignGalleryFiltersContext };
